'use client'

import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import Cta from '@/components/cta/cta';
import Close from '@/components/icon/close';
import OverlayArrowControls from '@/components/overlay-arrow-controls/overlay-arrow-controls';

export default function StoryOverlay({
  theme,
  previousRoute,
  nextRoute,
  closeHref,
  onNavigate,
  isVisible = true,
  showCloseButton = true,
  previousColor,
  bgRef,
  prevRef,
  nextRef,
  closeRef,
  children,
  hasLightControls = false,
  hasLightCloseCta = false,
  className = "",
  inert = false
}) {
  const overlayRef = useRef(null);

  const getFocusableElements = useCallback(() => {
    if (!overlayRef.current) return [];
    return [...overlayRef.current.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )];
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [getFocusableElements]);

  return (
    <div ref={overlayRef} className={className} data-story-theme={theme} inert={inert}>
      {/* Fixed background layer - starts with previous color if navigating between stories */}
      {/* before and after pseudo elements are used to create a 1px wide border on the left and right sides of the background layer */}
      {/* to prevent a subpixel bug during the slide-left/right page transition */}
      <div
        ref={bgRef}
        className="fixed inset-0 z-0 bg-story-theme transition-[background-color] motion-safe:duration-600 ease-[cubic-bezier(0.76,0,0.24,1)] before:absolute before:top-0 before:-left-px before:w-px before:h-full before:bg-[inherit] after:absolute after:top-0 after:-right-px after:w-px after:h-full after:bg-[inherit]"
        data-story-theme={theme}
        style={previousColor ? { backgroundColor: previousColor } : undefined}
        aria-hidden="true"
      />

      {showCloseButton && (
        <Cta
          ref={closeRef}
          className={clsx(
            'absolute z-50 top-[calc(2.5rem)] right-6 cta-icon-stroke-theme cta-circle-small pointer-events-auto lg:fixed lg:top-[calc(var(--space-8))] lg:right-(--space-8) lg:cta-circle transition-opacity motion-safe:duration-300',
            isVisible ? 'opacity-100' : 'opacity-0',
            hasLightCloseCta ? 'cta-light' : 'cta-dark',
          )}
          aria-label="Close"
          href={closeHref}
          transitionName="overlay-out"
        >
          <Close className="w-(--space-7) h-auto" aria-hidden="true" />
        </Cta>
      )}

      <div className="min-h-svh pt-8 pb-15 lg:pt-4 lg:pb-4 flex items-center justify-center relative">
        {children}
      </div>

      <OverlayArrowControls
        prevRef={prevRef}
        nextRef={nextRef}
        previousRoute={previousRoute}
        nextRoute={nextRoute}
        onClick={onNavigate}
        isVisible={isVisible}
        isLight={hasLightControls}
      />
    </div>
  );
}
